<template>
  <v-container fluid>
    <!-- Tabs Header -->
    <v-row justify="center" class="mb-6">
      <v-col cols="12" md="8" lg="6">
        <v-tabs
          v-model="activeTab"
          centered
          class="tabs-clean"
          background-color="#f0f0f0"
        >
          <v-tab value="games">Games</v-tab>
          <v-tab value="standings">Standings</v-tab>
        </v-tabs>
      </v-col>
    </v-row>

    <!-- Tabs Content -->
    <v-tabs-items v-model="activeTab">

      <!-- Games Table -->
      <v-tab-item value="games">
        <div v-if="activeTab === 'games'">

                    <!-- Filters -->
          <v-row class="mb-4" justify="center">
            <v-col cols="12" md="3">
              <v-select
                v-model="leagueGames"
                :items="leagues"
                item-title="name"
                item-value="id"
                label="League"
                variant="outlined"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                v-model="seasonGames"
                :items="filteredSeasonsGames"
                item-title="label"
                item-value="value"
                label="Season"
                variant="outlined"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                v-model="statusFilter"
                :items="statusOptions"
                item-title="label"
                item-value="value"
                label="Status"
                variant="outlined"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="searchQuery"
                label="Search teams..."
                variant="outlined"
                prepend-inner-icon="mdi-magnify"
                clearable
                hide-details
              />
            </v-col>
          </v-row>

          <!-- Current Date -->
          <v-row justify="center" class="mb-4">
            <v-col cols="12" class="text-center">
              <div class="current-date">{{ currentDate }}</div>
            </v-col>
          </v-row>

          <!-- Loading / Error States -->
          <v-row v-if="loadingFixtures">
            <v-col cols="12" class="text-center pa-4">
              Loading matches…
            </v-col>
          </v-row>
          <v-row v-else-if="errorFixtures">
            <v-col cols="12" class="text-error pa-4">
              {{ errorFixtures.message }}
            </v-col>
          </v-row>

          <!-- Games Data Table -->
          <v-row v-else>
            <v-col cols="12">

              <!-- Desktop Table -->
              <div v-if="!mobile" class="table-responsive">
                <v-data-table
                  :headers="headers"
                  :items="filteredFixtures"
                  item-key="fixture.id"
                  class="games-table"
                  :items-per-page="-1"
                >
                  <template #bottom></template>
                  <template v-slot:item.date="{ item }">
                    {{ formatDate(item.fixture.date) }}
                  </template>
                  <template v-slot:item.status="{ item }">
                    <span class="status-cell">{{ item.fixture.status.short }}</span>
                  </template>
                  <template v-slot:item.home="{ item }">
                    <div class="team-cell">
                      <v-avatar size="32" class="logo-avatar">
                        <v-img :src="item.teams.home.logo" :alt="item.teams.home.name" />
                      </v-avatar>
                      <span class="team-name">{{ item.teams.home.name }}</span>
                    </div>
                  </template>
                  <template v-slot:item.score="{ item }">
                    <span v-if="item.goals.home !== null" class="score-cell">
                      {{ item.goals.home }} – {{ item.goals.away }}
                    </span>
                    <span v-else class="score-cell">–</span>
                  </template>
                  <template v-slot:item.away="{ item }">
                    <div class="team-cell">
                      <v-avatar size="32" class="logo-avatar">
                        <v-img :src="item.teams.away.logo" :alt="item.teams.away.name" />
                      </v-avatar>
                      <span class="team-name">{{ item.teams.away.name }}</span>
                    </div>
                  </template>
                  <template v-slot:item.action="{ item }">
                    <div class="action-cell">
                      <v-btn text small class="view-button" @click.stop="openDialog(item)">
                        View Match
                      </v-btn>
                    </div>
                  </template>
                </v-data-table>
              </div>

              <!-- Mobile Cards -->
              <div v-else class="mobile-cards">
                <v-row>
                  <v-col
                    cols="12"
                    v-for="item in filteredFixtures"
                    :key="item.fixture.id"
                  >
                    <v-card
                      class="mobile-card"
               variant="outlined"
                      :style="{ backgroundImage: `url(${fieldBg})` }"
                    >
                      <div class="card-overlay"></div>
                      <v-card-text class="text-center">
                        <div class="mobile-stage">{{ item.league.round }}</div>
                        <div class="mobile-competition">{{ item.league.name }}</div>
                        <div class="mobile-date-status">
                          {{ formatDate(item.fixture.date) }}
                          <span v-if="item.fixture.status.short">
                            , {{ item.fixture.status.short }}
                          </span>
                        </div>
                        <div class="mobile-score-row">
                          <div class="mobile-logo-cell">
                            <v-avatar size="32" class="logo-avatar">
                              <v-img :src="item.teams.home.logo" :alt="item.teams.home.name" />
                            </v-avatar>
                            <div class="mobile-logo-name">{{ item.teams.home.name }}</div>
                          </div>
                          <div class="mobile-score-big">
                            <span v-if="item.goals.home !== null">
                              {{ item.goals.home }} – {{ item.goals.away }}
                            </span>
                            <span v-else>–</span>
                          </div>
                          <div class="mobile-logo-cell">
                            <v-avatar size="32" class="logo-avatar">
                              <v-img :src="item.teams.away.logo" :alt="item.teams.away.name" />
                            </v-avatar>
                            <div class="mobile-logo-name">{{ item.teams.away.name }}</div>
                          </div>
                        </div>
                        <v-btn text small class="view-button" @click.stop="openDialog(item)">
                          View Match
                        </v-btn>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </div>

            </v-col>
          </v-row>

        </div>
      </v-tab-item>

      <!-- Standings Widget -->
      <v-tab-item value="standings">
        <div v-if="activeTab === 'standings'">
          <v-row class="mb-4" justify="center">
            <v-col cols="12" md="4">
              <v-select
                v-model="leagueStandings"
                :items="leagues"
                item-title="name"
                item-value="id"
                label="League"
         variant="outlined"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="seasonStandings"
                :items="filteredSeasonsStandings"
                item-title="label"
                item-value="value"
                label="Season"
         variant="outlined"
              />
            </v-col>
          </v-row>
          <div
            id="wg-api-football-standings"
            class="wg_loader custom-theme"
            data-host="v3.football.api-sports.io"
            :data-key="API_KEY"
            :data-league="leagueStandings"
            :data-season="seasonStandings"
            data-theme=""
            data-show-errors="false"
            data-show-logos="true"
          ></div>
        </div>
      </v-tab-item>

    </v-tabs-items>

    <!-- Loader -->
    <div v-if="showLoader" class="main-fader">
      <div class="loader">
        <svg viewBox="0 0 866 866" xmlns="http://www.w3.org/2000/svg">
          <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 164.83 151.5">
            <path class="path-0" d="M117.24,69.24A8,8,0,0,0,115.67,67c-4.88-4-9.8-7.89-14.86-11.62A4.93,4.93,0,0,0,96.93,55c-5.76,1.89-11.4,4.17-17.18,6a4.36,4.36,0,0,0-3.42,4.12c-1,6.89-2.1,13.76-3,20.66a4,4,0,0,0,1,3.07c5.12,4.36,10.39,8.61,15.68,12.76a3.62,3.62,0,0,0,2.92.75c6.29-2.66,12.52-5.47,18.71-8.36a3.49,3.49,0,0,0,1.68-2.19c1.34-7.25,2.54-14.55,3.9-22.58Z" fill="#40c456" />
            <path class="path-1" d="M97.55,38.68A43.76,43.76,0,0,1,98,33.44c.41-2.36-.5-3.57-2.57-4.64C91.1,26.59,87,24,82.66,21.82a6.18,6.18,0,0,0-4-.71C73.45,22.55,68.32,24.25,63.22,26c-3.63,1.21-6.08,3.35-5.76,7.69a26.67,26.67,0,0,1-.6,4.92c-1.08,8.06-1.08,8.08,5.86,11.92,3.95,2.19,7.82,5.75,11.94,6.08s8.76-2.41,13.12-3.93c9.33-3.29,9.33-3.3,9.78-14Z" fill="#40c456" />
            <path class="path-2" d="M66.11,126.56c5.91-.91,11.37-1.7,16.81-2.71a3.3,3.3,0,0,0,1.87-2.17c1-4.06,1.73-8.19,2.84-12.24.54-2-.11-3-1.55-4.15-5-4-9.9-8.12-15-12a6.19,6.19,0,0,0-4.15-1.1c-5.35.66-10.7,1.54-16,2.54A4,4,0,0,0,48.34,97a109.13,109.13,0,0,0-3,12.19,4.47,4.47,0,0,0,1.34,3.6c5.54,4.36,11.23,8.53,16.91,12.69a10.84,10.84,0,0,0,2.57,1.11Z" fill="#40c456" />
            <path class="path-3" d="M127.42,104.12c4.1-2.1,8-3.93,11.72-6a6,6,0,0,0,2.27-3,58.22,58.22,0,0,0,3.18-29.92c-.26-1.7-8-7.28-9.71-6.85A5,5,0,0,0,133,59.65c-2.81,2.49-5.71,4.88-8.33,7.56a9.46,9.46,0,0,0-2.47,4.4c-1.29,6.49-2.38,13-3.35,19.55a5.73,5.73,0,0,0,.83,3.91c2.31,3.08,5,5.88,7.7,9Z" fill="#40c456" />
            <path class="path-4" d="M52.58,29.89c-2.15-.36-3.78-.54-5.39-.9-2.83-.64-4.92.1-7,2.32A64.1,64.1,0,0,0,26.09,54.64c-2.64,7.92-2.62,7.84,5.15,10.87,1.76.69,2.73.45,3.93-1C39.79,59,44.54,53.65,49.22,48.2a4.2,4.2,0,0,0,1.13-2c.8-5.32,1.49-10.68,2.24-16.34Z" fill="#40c456" />
            <path class="path-5" fill="#40c456" d="M23,68.13c0,2.51,0,4.7,0,6.87a60.49,60.49,0,0,0,9.75,32.15c1.37,2.13,6.4,3,7,1.2,1.55-5,2.68-10.2,3.82-15.34.13-.58-.58-1.38-.94-2.06-2.51-4.77-5.47-9.38-7.45-14.37C32.94,71,28.22,69.84,23,68.13Z" />
            <path class="path-6" fill="#40c456" d="M83.91,12.86c-.32.36-.66.71-1,1.07.9,1.13,1.57,2.62,2.73,3.33,4.71,2.84,9.56,5.48,14.39,8.1a9.29,9.29,0,0,0,3.13.83c5.45.69,10.89,1.38,16.35,1.94a10.41,10.41,0,0,0,3.07-.71c-11.48-9.9-24.26-14.61-38.71-14.56Z" />
            <path class="path-7" fill="#40c456" d="M66.28,132.51c13.36,3.78,25.62,3.5,38-.9C91.68,129.59,79.36,128,66.28,132.51Z" />
            <path class="path-8" fill="#40c456" d="M127.2,30.66l-1.27.37a18.58,18.58,0,0,0,1,3.08c3,5.52,6.21,10.89,8.89,16.54,1.34,2.83,3.41,3.82,6.49,4.9a60.38,60.38,0,0,0-15.12-24.9Z" />
            <path class="bb-9" fill="#40c456" d="M117.35,125c5.58-2.32,16.9-13.84,18.1-19.2-2.41,1.46-5.18,2.36-6.78,4.23-4.21,5-7.89,10.37-11.32,15Z" />
          </svg>
        </svg>
      </div>
    </div>

    <!-- Game Detail Dialog -->
    <div v-if="dialog" class="notification-overlay" @click.self="dialog = false">
      <div class="notification-dialog">
        <!-- colored card behind -->
        <div class="notification-dialog-bg success"></div>
        <!-- main white card -->
        <div class="notification-dialog-inner">
          <!-- Icon positioned outside the card -->
          <div style="margin-left: -50px;" class="notification-icon success">
            <v-icon>mdi-soccer</v-icon>
          </div>

          <!-- Close button outside scroll area -->
          <button class="notification-close" @click="dialog = false">&times;</button>

          <!-- Fixed header -->
          <h2 class="notification-title my-7">Game Details</h2>
          
          <!-- Scrollable content area -->
          <div class="notification-content ">
            <!-- Single-game widget -->
            <div
              id="wg-api-football-game"
              :key="selectedFixtureKey"
              class="wg_loader custom-theme dialog-widget"
              data-host="v3.football.api-sports.io"
              :data-key="API_KEY"
              :data-id="selectedFixture?.fixture.id"
              :data-league="leagueGames"
              :data-season="seasonGames"
              data-theme=""
              data-refresh="15"
              data-show-errors="false"
              data-show-logos="true"
            ></div>
            
            <!-- Head-to-Head Results as Cards -->
            <div v-if="headToHead.length" class="mt-6 h2h-cards">
              <h3 class="mb-4">Head-to-Head Results</h3>
              <v-row>
                <v-col
                  cols="12"
                  sm="6"
                  md="6"
                  v-for="h in headToHead"
                  :key="h.fixture.id"
                >
                  <v-card
                    class="h2h-card"
             variant="outlined"
                    :style="{ backgroundImage: `url(${fieldBg})` }"
                  >
                    <div class="card-overlay"></div>
                    <v-card-text class="py-6 px-4 text-center">
                      <div class="h2h-date">{{ formatDate(h.fixture.date) }}</div>
                      <div class="h2h-content">
                        <div class="h2h-row">
                          <v-avatar size="40" class="badge-avatar">
                            <v-img :src="h.teams.home.logo" :alt="h.teams.home.name" />
                          </v-avatar>
                          <span class="h2h-score">
                            {{ h.goals.home }} – {{ h.goals.away }}
                          </span>
                          <v-avatar size="40" class="badge-avatar">
                            <v-img :src="h.teams.away.logo" :alt="h.teams.away.name" />
                          </v-avatar>
                        </div>
                        <div class="h2h-row-names">
                          <span class="team-name">{{ h.teams.home.name }}</span>
                          <span class="team-name">{{ h.teams.away.name }}</span>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </div>
            <v-row v-else-if="loadingH2H">
              <v-col cols="12" class="text-center pa-4">
                Loading Head-to-Head data…
              </v-col>
            </v-row>
            <v-row v-else-if="errorH2H">
              <v-col cols="12" class="text-error pa-4">
                {{ errorH2H.message }}
              </v-col>
            </v-row>
            <v-row v-else>
              <v-col cols="12" class="text-center pa-4">
                No head-to-head data available.
              </v-col>
            </v-row>
          </div>
          
          <!-- Close button outside scroll area -->
          <button class="notification-button success" @click="dialog = false">
            Close
          </button>
        </div>
      </div>
    </div>
  </v-container>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useDisplay } from 'vuetify'
import axios from 'axios'
import fieldBg from '@/assets/field.png'

const { mobile } = useDisplay()
const API_KEY = '7729da2e4edde193eb1e2c62c5ca8fd0'

const leagues = [
  { name: 'PSL (South Africa)', id: 288 },
  { name: 'CAF Champions League', id: 12 },
  { name: 'CAF Confederation Cup', id: 20 },
    { name: 'Africa Cup of Nations', id: 6 },
  { name: 'Motsepe league', id: 289 },
  { name: 'MTN 8 (South Africa)', id: 509 },
  { name: 'Lesotho Premier League', id: 962 },
  { name: 'Botswana Premier League', id: 412 },
  { name:'Eswatini Premier League', id: 847 },
  { name: 'Tanzania Premier League', id: 567	 },
  { name: 'Egypt Premier League', id: 233		 },
  { name:'Moroccan Botola league', id: 200	 },
  { name:'English Premier League', id: 39 },
  { name:'MLS', id: 253 },
  { name: 'La Liga', id: 140 },
  { name: 'Ligue 1', id: 61 },
  { name: 'Serie A', id: 135 },
  { name: 'Bundesliga', id: 78 },
  { name: 'Club World Cup', id: 15 },
  { name: 'COSAFA Cup', id: 859 },
  { name: 'Champions League', id: 2 },
  { name: 'Europa League', id: 3 },
  { name: 'Conference League', id: 848 },
  { name: 'UEFA Nations League', id: 5 },
  { name:'Primeira Liga', id: 94 },
]

const seasons = [
  { label: '24-25', value: 2024 },
  { label: '25-26', value: 2025 }
]

const statusOptions = [
  { label: 'All', value: 'all' },
  { label: 'Live', value: 'live' },
  { label: 'Finished', value: 'finished' },
  { label: 'Scheduled', value: 'scheduled' }
]

const activeTab = ref('games')
const leagueGames = ref(leagues[0].id)
const seasonGames = ref(leagueGames.value === 15 ? 2025 : 2025)
const statusFilter = ref('all')
const searchQuery = ref('')
// Show current date in Africa/Johannesburg to avoid UTC year/day drift
const currentDate = computed(() => {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Johannesburg',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(new Date())
})

const leagueStandings = ref(leagues[0].id)
const seasonStandings = ref(leagueStandings.value === 15 ? 2025 : 2025)

const filteredSeasonsGames = computed(() =>
  leagueGames.value === 15 ? seasons.filter(s => s.value === 2025) : seasons
)
const filteredSeasonsStandings = computed(() =>
  leagueStandings.value === 15 ? seasons.filter(s => s.value === 2025) : seasons
)

watch(leagueGames, v => (seasonGames.value = v === 15 ? 2025 : 2025))
watch(leagueStandings, v => (seasonStandings.value = v === 15 ? 2025 : 2025))

const headers = [
  { title: 'Date', align: 'center', key: 'date' },
  { title: 'Status', align: 'center', key: 'status' },
  { title: 'Home', align: 'center', key: 'home' },
  { title: 'Score', align: 'center', key: 'score' },
  { title: 'Away', align: 'center', key: 'away' },
  { title: 'Actions', align: 'center', key: 'action', width: '120px' }
]

const api = axios.create({
  baseURL: 'https://v3.football.api-sports.io',
  headers: { 'x-apisports-key': API_KEY }
})

const fixtures = ref([])
const loadingFixtures = ref(false)
const errorFixtures = ref(null)

const headToHead = ref([])
const loadingH2H = ref(false)
const errorH2H = ref(null)

const dialog = ref(false)
const showLoader = ref(false)
const selectedFixture = ref(null)
const selectedFixtureKey = ref('')

const fixturesCache = new Map()
const h2hCache = new Map()
// Track active H2H request key to avoid race conditions
const h2hActiveKey = ref(null)
// Track a pending dialog loader timer so we can cancel when switching quickly
let dialogTimerId = null
const lastGameWidgetKey = ref(null)
const lastStandingsWidgetKey = ref(null)

const filteredFixtures = computed(() => {
  const TZ = 'Africa/Johannesburg'
  // Helper to get Y/M/D parts in a specific timezone
  const dtf = new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
  const getYMD = (dateObj) => {
    const parts = dtf.formatToParts(dateObj)
    const y = Number(parts.find(p => p.type === 'year')?.value || 0)
    const m = Number(parts.find(p => p.type === 'month')?.value || 0)
    const d = Number(parts.find(p => p.type === 'day')?.value || 0)
    return { y, m, d }
  }

  // Build reference range: today, this week (Mon..Sun), and this month
  const now = new Date()
  const today = getYMD(now)
  const todayUTC = Date.UTC(today.y, today.m - 1, today.d)
  const dayMs = 24 * 60 * 60 * 1000
  const dow = new Date(todayUTC).getUTCDay() // 0=Sun .. 6=Sat
  const mondayUTC = todayUTC - ((dow + 6) % 7) * dayMs
  const sundayUTC = mondayUTC + 6 * dayMs

  const sameMonth = (ymd) => ymd.y === today.y && ymd.m === today.m

  const bucketOf = (f) => {
    const d = new Date(f.fixture.date)
    const ymd = getYMD(d)
    const u = Date.UTC(ymd.y, ymd.m - 1, ymd.d)
    if (u === todayUTC) return 0 // Today first
    if (u > todayUTC && u <= sundayUTC) return 1 // Rest of this week
    if (u > sundayUTC && sameMonth(ymd)) return 2 // Rest of this month
    return 3 // Everything else
  }

  const timeAsc = (a, b) => new Date(a.fixture.date) - new Date(b.fixture.date)

  const FINISHED_STATUSES = ['FT', 'AET', 'PEN']
  const LIVE_STATUSES = ['1H', '2H', 'HT', 'LIVE']

  return fixtures.value
    .filter(f => {
      // Status filter
      const s = f.fixture.status.short
      let statusMatch = true
      if (statusFilter.value === 'all') statusMatch = true
      else if (statusFilter.value === 'live') statusMatch = LIVE_STATUSES.includes(s)
      else if (statusFilter.value === 'finished') statusMatch = FINISHED_STATUSES.includes(s)
      else if (statusFilter.value === 'scheduled') statusMatch = ['NS', 'TBD'].includes(s)

      // Search filter
      const searchLower = searchQuery.value.toLowerCase()
      const homeTeam = f.teams.home.name ? f.teams.home.name.toLowerCase() : ''
      const awayTeam = f.teams.away.name ? f.teams.away.name.toLowerCase() : ''
      const searchMatch = !searchQuery.value || homeTeam.includes(searchLower) || awayTeam.includes(searchLower)

      return statusMatch && searchMatch
    })
    .sort((a, b) => {
      // For finished-only view, show most recent first
      if (statusFilter.value === 'finished') {
        return new Date(b.fixture.date) - new Date(a.fixture.date)
      }
      const ba = bucketOf(a)
      const bb = bucketOf(b)
      if (ba !== bb) return ba - bb
      // Within each bucket, sort by kickoff ascending
      return timeAsc(a, b)
    })
})

function debounce(fn, delay = 400) {
  let timeout
  return (...args) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => fn(...args), delay)
  }
}

const fetchFixtures = async () => {
  const key = `${leagueGames.value}|${seasonGames.value}`
  if (fixturesCache.has(key)) {
    fixtures.value = fixturesCache.get(key)
    return
  }
  loadingFixtures.value = true
  errorFixtures.value = null
  try {
    console.log('[test3] fetching /fixtures with', {
      league: leagueGames.value,
      season: seasonGames.value,
      timezone: 'Africa/Johannesburg'
    })
    const res = await api.get('/fixtures', {
      params: {
        league: leagueGames.value,
        season: seasonGames.value,
        timezone: 'Africa/Johannesburg'
      }
    })
    console.log('[test3] /fixtures response', res)
    console.log('[test3] /fixtures data', res?.data)
    console.log('[test3] /fixtures count', Array.isArray(res?.data?.response) ? res.data.response.length : 'n/a')
    fixturesCache.set(key, res.data.response)
    fixtures.value = res.data.response
  } catch (err) {
    console.error('[test3] /fixtures error', {
      message: err?.message,
      status: err?.response?.status,
      data: err?.response?.data
    })
    errorFixtures.value = err
  } finally {
    loadingFixtures.value = false
    nextTick(sendHeight)
  }
}
const debouncedFetchFixtures = debounce(fetchFixtures, 400)

async function fetchHeadToHeadImpl(homeId, awayId) {
  const key = `${homeId}-${awayId}`
  h2hActiveKey.value = key
  // If cached, use immediately but still mark as not loading
  if (h2hCache.has(key)) {
    headToHead.value = h2hCache.get(key)
    loadingH2H.value = false
    errorH2H.value = null
    return
  }
  loadingH2H.value = true
  errorH2H.value = null
  try {
    console.log('[test3] fetching /fixtures/headtohead with', { h2h: key })
    const res = await api.get('/fixtures/headtohead', { params: { h2h: key } })
    console.log('[test3] /fixtures/headtohead response', res)
    console.log('[test3] /fixtures/headto-head data', res?.data)
    // Only apply if this response matches the latest requested key
    if (h2hActiveKey.value === key) {
      h2hCache.set(key, res.data.response)
      headToHead.value = res.data.response
    }
  } catch (err) {
    console.error('[test3] /fixtures/headtohead error', {
      message: err?.message,
      status: err?.response?.status,
      data: err?.response?.data
    })
    // Only surface error if still relevant
    if (h2hActiveKey.value === key) {
      errorH2H.value = err
    }
  } finally {
    if (h2hActiveKey.value === key) {
      loadingH2H.value = false
      nextTick(sendHeight)
    }
  }
}
const fetchHeadToHead = debounce(fetchHeadToHeadImpl, 300)

function initGameWidget() {
  if (!selectedFixture.value) return
  const id = selectedFixture.value.fixture.id
  const league = leagueGames.value
  const season = seasonGames.value
  const key = `${id}|${league}|${season}`
  // Always (re)initialize to be bulletproof when switching matches
  lastGameWidgetKey.value = key
  const el = document.getElementById('wg-api-football-game')
  if (!el) return
  el.innerHTML = ''
  el.setAttribute('data-id', id)
  el.setAttribute('data-league', league)
  el.setAttribute('data-season', season)
  // The widgets library listens on window's DOMContentLoaded, not document
  window.dispatchEvent(new Event('DOMContentLoaded'))
  // Fire again on next tick to cover async DOM updates
  setTimeout(() => window.dispatchEvent(new Event('DOMContentLoaded')), 0)
}

function updateStandingsWidget(forceReinit = false) {
  const league = leagueStandings.value
  const season = seasonStandings.value
  const key = `${league}|${season}`
  if (!forceReinit && lastStandingsWidgetKey.value === key) return
  lastStandingsWidgetKey.value = key
  nextTick(() => {
    const el = document.getElementById('wg-api-football-standings')
    if (!el) return
    el.innerHTML = ''
    el.setAttribute('data-league', league)
    el.setAttribute('data-season', season)
    window.dispatchEvent(new Event('DOMContentLoaded'))
    setTimeout(() => window.dispatchEvent(new Event('DOMContentLoaded')), 100)
    sendHeight()
  })
}

function openDialog(fixture) {
  // Cancel any pending loader from a previous click
  if (dialogTimerId) {
    clearTimeout(dialogTimerId)
    dialogTimerId = null
  }

  // Set the selected fixture immediately
  selectedFixture.value = fixture
  // Force a hard remount of the widget container every open/switch
  selectedFixtureKey.value = `${fixture.fixture.id}|${leagueGames.value}|${seasonGames.value}|${Date.now()}`

  // Reset H2H state to avoid showing previous teams while loading
  headToHead.value = []
  errorH2H.value = null
  loadingH2H.value = true

  showLoader.value = true

  // Show loader for 2 seconds before opening dialog
  dialogTimerId = setTimeout(() => {
    showLoader.value = false
    dialog.value = true
    const homeId = fixture.teams.home.id
    const awayId = fixture.teams.away.id
    if (homeId && awayId) {
      fetchHeadToHead(homeId, awayId)
    } else {
      loadingH2H.value = false
    }
    nextTick(() => {
      initGameWidget()
      sendHeight()
    })
    dialogTimerId = null
  }, 2000)
}

onMounted(() => {
  sendHeight()
  window.addEventListener('resize', sendHeight)
  if (typeof ResizeObserver !== 'undefined') {
    heightResizeObserver = new ResizeObserver(() => sendHeight())
    heightResizeObserver.observe(document.body)
    const appEl = document.getElementById('app')
    if (appEl) heightResizeObserver.observe(appEl)
  }
  debouncedFetchFixtures()
  const src = 'https://widgets.api-sports.io/2.0.3/widgets.js'
  if (!document.querySelector(`script[src="${src}"]`)) {
    const s = document.createElement('script')
    s.type = 'module'
    s.src = src
    document.body.appendChild(s)
  }
})

watch([leagueGames, seasonGames], debouncedFetchFixtures)

watch(leagueStandings, () => {
  if (activeTab.value === 'standings') updateStandingsWidget()
})
watch(seasonStandings, () => {
  if (activeTab.value === 'standings') updateStandingsWidget()
})
watch(activeTab, (val, prev) => {
  if (val === 'standings') {
    updateStandingsWidget()
    nextTick(() => {
      window.scrollTo(0, 0)
      const vMain = document.querySelector('.v-main')
      if (vMain) vMain.style.height = 'auto'
      sendHeight()
      setTimeout(() => {
        const newHeight = document.documentElement.scrollHeight
        if (window.parent !== window) {
          window.parent.postMessage({ type: 'setHeight', height: newHeight }, '*')
        }
      }, 300)
      setTimeout(sendHeight, 500)
      setTimeout(sendHeight, 1200)
    })
  } else {
    if (prev === 'standings') lastStandingsWidgetKey.value = null
    nextTick(() => {
      const vMain = document.querySelector('.v-main')
      if (vMain) vMain.style.height = 'auto'
      sendHeight()
      setTimeout(sendHeight, 300)
    })
  }
})

function formatDate(iso) {
  return new Date(iso).toLocaleString('en-ZA', {
    timeZone: 'Africa/Johannesburg',
    dateStyle: 'medium',
    timeStyle: 'short'
  })
}

// Notify parent (e.g. iframe host) of content height so iframe can expand with table/content
function getContentHeight() {
  const body = document.body
  const html = document.documentElement
  const app = document.getElementById('app')
  return Math.max(
    body?.scrollHeight ?? 0,
    body?.offsetHeight ?? 0,
    html?.scrollHeight ?? 0,
    html?.offsetHeight ?? 0,
    app?.scrollHeight ?? 0,
    app?.offsetHeight ?? 0
  )
}
function sendHeight() {
  if (window.parent === window) return
  const height = getContentHeight()
  window.parent.postMessage({ type: 'setHeight', height }, '*')
}

let heightResizeObserver = null

onUnmounted(() => {
  window.removeEventListener('resize', sendHeight)
  if (heightResizeObserver) {
    heightResizeObserver.disconnect()
    heightResizeObserver = null
  }
})
</script>

<style scoped>
/* Tabs */
.tabs-clean {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
}
.tabs-clean .v-tab {
  flex: 1;
  text-align: center;
  padding: 12px 0;
  margin: 0 4px;
  border-radius: 4px;
  color: #2c2f34;
  font-weight: 500;
}
.tabs-clean .v-tab--active {
  background-color: #175a40 !important;
  color: #fff !important;
}

/* Date pill */
.current-date {
  font-weight: 700;
  color: #fff;
  background: #175a40;
  padding: 12px 24px;
  border-radius: 25px;
  display: inline-block;
  font-size: 18px;
  min-width: auto;
  max-width: 300px;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 4px 12px rgba(23, 90, 64, 0.3);
}

/* Desktop table */
.table-responsive {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.games-table {
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.games-table th {
  background: #175a40 !important;
  color: #fff !important;
  text-transform: none;
  padding: 16px 12px;
  white-space: nowrap;
  border-bottom: 2px solid #e0e0e0;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0.5px;
}

.games-table td {
  padding: 12px;
  border-bottom: 1px solid #e0e0e0;
  color: #2c2f34;
}
.logo-avatar {
  overflow: visible !important;
  border-radius: 50%;
}
.team-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}
.team-cell .team-name {
  margin-left: 8px;
  font-size: 14px;
  font-weight: 500;
}
.score-cell {
  text-align: center;
  font-weight: bold;
  white-space: nowrap;
  font-size: 16px;
}

.status-cell {
  text-align: center;
  font-weight: 500;
}

.action-cell {
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Buttons */
.view-button {
  background-color: #175a40 !important;
  color: #fff !important;
  font-weight: 600;
  border-radius: 8px !important;
}

/* Notification Dialog */
.notification-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.notification-dialog {
  position: relative;
  width: 820px;
  max-width: 90vw;
  min-height: 200px;
}

.notification-dialog-bg {
  position: absolute;
  top: 12px;
  left: 12px;
  width: 96%;
  height: 100%;
  border-radius: 16px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.15);
}
.notification-dialog-bg.success {
  background-color: #175a40;
}

.notification-dialog-inner {
  position: relative;
  background: #ffffff;
  border-radius: 16px;
  padding: 15px;
  text-align: center;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.15);
  max-height: 90vh;
  padding-top: 60px;
  padding-bottom: 60px;
}

.notification-close {
  position: absolute;
  top: 16px;
  right: 16px;
  background: transparent;
  border: none;
  font-size: 24px;
  cursor: pointer;
  line-height: 1;
  z-index: 15;
}

.notification-icon {
  position: absolute;
  top: -40px;
  left: 50%;
  transform: translateX(-50%);
  width: 100px;
  height: 100px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeInCircle 0.3s ease-out forwards;
  z-index: 20;
}

.notification-content {
  max-height: calc(90vh - 250px);
  overflow-y: auto;
  padding: 0 10px;
}

.notification-content::-webkit-scrollbar {
  width: 8px;
}

.notification-content::-webkit-scrollbar-track {
  background: transparent;
}

.notification-content::-webkit-scrollbar-thumb {
  background-color: #175a40;
  border-radius: 4px;
}

.notification-content {
  scrollbar-width: thin;
  scrollbar-color: #175a40 transparent;
}
.notification-icon.success {
  background-color: #175a40;
}
.notification-icon .v-icon {
  font-size: 50px;
  color: #ffffff;
}
.notification-icon::before {
  content: '';
  position: absolute;
  width: 120px;
  height: 120px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  opacity: 0.2;
  animation: pulse 2s ease-in-out infinite;
  z-index: -1;
}
.notification-icon.success::before {
  background-color: #175a40;
}

@keyframes fadeInCircle {
  from {
    opacity: 0;
    transform: scale(0.7);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes pulse {
  0%, 100% {
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    transform: translate(-50%, -50%) scale(1.2);
  }
}

.notification-title {
  font-size: 2em;
  margin: 0 0 12px 0;
}

.notification-button {
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-size: 1.1em;
  padding: 12px 24px;
  cursor: pointer;
  margin-top: 20px;
}
.notification-button.success {
  background-color: #175a40;
}
.notification-button:hover {
  opacity: 0.9;
}

/* Dialog Widget */
.dialog-widget {
  width: 100%;
  max-width: 760px;
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 24px;
}
.custom-theme ::v-deep(.wg_loader),
.custom-theme ::v-deep(.wg_header),
.custom-theme ::v-deep(.wg_table th),
.custom-theme ::v-deep(.wg_btn),
.custom-theme ::v-deep(.wg_toolbar) {
  background: #175a40 !important;
  color: #fff !important;
}

/* Shared card overlay */
.card-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.45);
  border-radius: 16px;
  pointer-events: none;
}

/* Mobile fixture cards */
.mobile-cards {
  padding: 0 12px;
}
.mobile-card {
  margin-bottom: 16px;
  position: relative;
  background-size: cover !important;
  background-position: center !important;
  color: #fff !important;
  box-shadow: 0 4px 10px rgba(0,0,0,0.15);
  border-radius: 16px !important;
  padding: 16px !important;
}
.mobile-card .v-card-text {
  position: relative;
  z-index: 1;
  padding: 0 !important;
}
.mobile-stage {
  font-size: 12px;
  text-transform: uppercase;
  margin-bottom: 6px;
  font-weight: 600;
}
.mobile-competition {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 6px;
}
.mobile-date-status {
  font-size: 12px;
  margin-bottom: 14px;
}

/* Mobile score + logos + names */
.mobile-score-row {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}
.mobile-logo-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 20px;
}
.mobile-score-big {
  font-size: 24px;
  font-weight: bold;
  margin: 0 12px;
  text-align: center;
  white-space: nowrap;
}
@media (max-width: 600px) {
  .mobile-score-big {
    font-size: 20px;
  }
}
@media (max-width: 400px) {
  .mobile-score-big {
    font-size: 16px;
  }
}
.mobile-logo-name {
  font-size: 11px;
  text-align: center;
  max-width: 80px;
  word-wrap: break-word;
}

/* Loader */
.main-fader {
  width: 100%;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 9999;
}

.main-fader .loader {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.main-fader .loader svg {
  height: 300px;
  display: block;
  margin: 0 auto;
}

.main-fader .loader svg path {
  animation-duration: 1s;
  animation-name: pulse;
  animation-iteration-count: infinite;
  color: #26a380;
}

.main-fader .loader svg path.path-7 {
  animation-delay: -1s;
}

.main-fader .loader svg path.path-6 {
  animation-delay: -0.875s;
}

.main-fader .loader svg path.path-5 {
  animation-delay: -0.75s;
}

.main-fader .loader svg path.path-4 {
  animation-delay: -0.625s;
}

.main-fader .loader svg path.path-3 {
  animation-delay: -0.5s;
}

.main-fader .loader svg path.path-2 {
  animation-delay: -0.375s;
}

.main-fader .loader svg path.path-1 {
  animation-delay: -0.25s;
}

.main-fader .loader svg path.path-0 {
  animation-delay: -0.125s;
}

@keyframes pulse {
  0% {
    opacity: 0.1;
  }
  30% {
    opacity: 0.8;
  }
  100% {
    opacity: 0.1;
  }
}

/* Head-to-head cards */
.h2h-cards {
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
}
.h2h-card {
  margin-bottom: 16px;
  position: relative;
  background-size: cover !important;
  background-position: center !important;
  color: #fff !important;
  box-shadow: 0 4px 10px rgba(0,0,0,0.1);
  border-radius: 16px !important;
}
.h2h-card .v-card-text {
  position: relative;
  z-index: 1;
}
.h2h-date {
  font-size: 12px;
  margin-bottom: 12px;
}
.h2h-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: max-content;
  margin: 0 auto;
  gap: 8px;
}
.h2h-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}
.h2h-row-names {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}
.h2h-score {
  font-size: 20px;
  font-weight: bold;
  white-space: nowrap;
}
.team-name {
  font-size: 14px;
  font-weight: 600;
}
@media (max-width: 600px) {
  .h2h-score {
    font-size: 18px;
  }
}
@media (max-width: 400px) {
  .h2h-score {
    font-size: 16px;
  }
}
</style>
